import { computed, ref, type Ref, type WritableComputedRef } from 'vue';
import { debounce } from '../helpers';

const DEFAULT_DEBOUNCE_DELAY = 300;

interface UseDebouncedModelReturn<T> {
    model: WritableComputedRef<T>;
    isDirty: Ref<boolean>;
}

/**
 * Creates a debounced computed model that delays setter execution.
 * 
 * @param source - Ref or WritableComputedRef to wrap with debounced setter
 * @param delay - Debounce delay in milliseconds (default: 300)
 * @returns Object containing the computed model and isDirty state
 */
function useDebouncedModel<T>(
    source: Ref<T> | WritableComputedRef<T>,
    delay: number = DEFAULT_DEBOUNCE_DELAY
): UseDebouncedModelReturn<T> {
    const isDirty = ref<boolean>(false);

    const debouncedSetter = debounce((value: T) => {
        isDirty.value = true;
        source.value = value;
    }, delay);

    const model = computed<T>({
        get: () => source.value,
        set: (value: T) => {
            debouncedSetter(value);
        }
    });

    return {
        model,
        isDirty
    };
}

export {
    useDebouncedModel
};
